import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlined";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { Box, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { recurringShiftsPath } from "../../utils/recurringShiftsRoutes";
import { HiringAIRecommend } from "./components/HiringAIRecommend";
import { HiringSimulateDialog } from "./components/HiringSimulateDialog";
import { HiringStaffingTable } from "./components/HiringStaffingTable";
import { HiringSummaryCards } from "./components/HiringSummaryCards";
import { StaffingParameters } from "./components/StaffingParameters";
import { type UnderutilizedNurse, useHiringData } from "./hooks/useHiringData";
import { useSimulation } from "./hooks/useSimulation";
import type { ShiftFilter } from "./types";

const DemoHiringModule: FC = () => {
	const navigate = useNavigate();
	const { departmentId } = useParams<{ departmentId: string }>();
	const [aiEnabled, setAiEnabled] = useState(false);
	const [isParamsOpen, setIsParamsOpen] = useState(true);
	const [callOffCount, setCallOffCount] = useState(0);
	const [ptoDaily, setPtoDaily] = useState(0);
	const {
		census,
		setCensus,
		targetHppd,
		setTargetHppd,
		dates,
		getShiftsForDate,
		shiftStats,
		predictedHppd,
		calculateNeeded,
		missingShifts,
		hospitalData,
		aiRecommendation,
		underutilizedNurses,
	} = useHiringData({ includeAIRelated: aiEnabled });

	const { simulationData, resetSimulation, addExistingToSimulation, addHiresToSimulation, addCustomSimulation } = useSimulation({
		dates,
		calculateNeeded,
		getShiftsForDate,
	});

	const [showRecommendations, setShowRecommendations] = useState(false);
	const [loading, setLoading] = useState(false);
	const [simulateDialogOpen, setSimulateDialogOpen] = useState(false);
	const recommendRef = useRef<HTMLDivElement>(null);
	const tableRef = useRef<HTMLDivElement>(null);

	const scrollToTable = useCallback(() => {
		setTimeout(() => {
			tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
		}, 100);
	}, []);

	useEffect(() => {
		setShowRecommendations(false);
		resetSimulation();
	}, [resetSimulation]);

	const handleGenerate = useCallback(() => {
		setAiEnabled(true);
		setLoading(true);
		setShowRecommendations(true);
		resetSimulation();
		setTimeout(() => {
			setLoading(false);
			recommendRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
		}, 2000);
	}, [resetSimulation]);

	const handleAcceptExisting = useCallback(
		(role: string, filter: ShiftFilter, nurses: UnderutilizedNurse[]) => {
			addExistingToSimulation(role, filter, nurses);
			scrollToTable();
		},
		[addExistingToSimulation, scrollToTable],
	);

	const handleAcceptHire = useCallback(
		(role: string, count: number, filter: ShiftFilter) => {
			addHiresToSimulation(role, count, filter);
			scrollToTable();
		},
		[addHiresToSimulation, scrollToTable],
	);

	const handleSimulate = useCallback(
		(role: string, shiftFilter: ShiftFilter, nurseName: string, selectedDates: DateTime[]) => {
			addCustomSimulation(role, shiftFilter, nurseName, selectedDates);
			scrollToTable();
		},
		[addCustomSimulation, scrollToTable],
	);

	return (
		<Box
			data-testid="hiring-root"
			sx={{
				width: "100%",
				height: "calc(100vh - 50px)",
				px: 4,
				py: 2,
				overflow: "auto",
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 2,
					pb: 2,
				}}
			>
				<SystemButton
					buttonVariant={"secondary"}
					leadingIcon={<ArrowBackIcon />}
					onClick={() => navigate(departmentId ? recurringShiftsPath(departmentId) : "/")}
				>
					Back
				</SystemButton>
				<Typography
					variant="h3"
					color="primary"
					sx={{
						fontWeight: 600,
					}}
				>
					Hiring Module
				</Typography>
			</Box>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: 2,
					pb: 4,
				}}
			>
				<StaffingParameters
					census={census}
					setCensus={setCensus}
					targetHppd={targetHppd}
					setTargetHppd={setTargetHppd}
					callOffCount={callOffCount}
					setCallOffCount={setCallOffCount}
					ptoDaily={ptoDaily}
					setPtoDaily={setPtoDaily}
					isOpen={isParamsOpen}
					onToggleOpen={() => setIsParamsOpen((prev) => !prev)}
				/>

				<HiringSummaryCards
					census={census}
					targetHppd={targetHppd}
					shiftStats={shiftStats}
					predictedHppd={predictedHppd}
					missingShifts={missingShifts}
					callOffCount={callOffCount}
					ptoDaily={ptoDaily}
				/>

				<Box sx={{ display: "flex", justifyContent: "flex-start", gap: 2 }}>
					<SystemButton
						buttonVariant={"primary"}
						leadingIcon={showRecommendations && !loading ? <CheckCircleOutlineIcon /> : <AutoAwesomeIcon />}
						data-testid="hiring-ai-recommend"
						onClick={handleGenerate}
					>
						AI Recommend
					</SystemButton>
					<SystemButton
						buttonVariant={"secondary"}
						leadingIcon={<PlayArrowIcon />}
						data-testid="hiring-add-simulation"
						onClick={() => setSimulateDialogOpen(true)}
					>
						Add Simulation
					</SystemButton>
				</Box>

				<HiringStaffingTable
					ref={tableRef}
					dates={dates}
					calculateNeeded={calculateNeeded}
					getShiftsForDate={getShiftsForDate}
					nurses={hospitalData.nurses}
					simulationData={simulationData}
				/>

				{showRecommendations && (
					<Box ref={recommendRef}>
						<HiringAIRecommend
							recommendations={aiRecommendation}
							loading={loading}
							underutilizedNurses={underutilizedNurses}
							onAcceptExisting={handleAcceptExisting}
							onAcceptHire={handleAcceptHire}
						/>
					</Box>
				)}

				<HiringSimulateDialog
					open={simulateDialogOpen}
					onClose={() => setSimulateDialogOpen(false)}
					dates={dates}
					weekNumbers={[1, 2]}
					onSimulate={handleSimulate}
				/>
			</Box>
		</Box>
	);
};

export default DemoHiringModule;
